import React, { useState, useEffect, useRef } from 'react';
import MobileDashboard from './MobileDashboard';
import MobileAsCalendar from './MobileAsCalendar';
import MobileAnalysis from './MobileAnalysis';
import MobileRecall from './MobileRecall';
import { asService } from '../../services/asService';
import { partService } from '../../services/partService';
import { customerService } from '../../services/customerService';
import { vehicleService } from '../../services/vehicleService';
import { recallService } from '../../services/recallService';
import { engineerService } from '../../services/engineerService';
import ChangePasswordModal from '../../components/common/ChangePasswordModal';
import MobileSystemNav from '../../components/mobile/MobileSystemNav';
import useAppVersion from '../../hooks/useAppVersion';

// 🚀 [성능] 모바일 데이터 캐시 — 지난번에 받은 것을 즉시 보여주고 뒤에서 갱신한다.
//
// AS 이력은 전부 받는다. (2026-09-23)
//   예전에는 '최신 500건만' 받았는데, 정작 홈 화면이 따로 전량을 한 번 더 받고 있어서
//   아끼는 것도 없이 캘린더·분석만 500건에서 잘려 나갔다. 특히 분석의 '전체 누적 매출'은
//   모든 완료 건을 더해야 맞는 값이라, 500건을 넘기는 순간 조용히 틀린 금액이 됐을 것이다.
//   이제 여기서 한 번만 받아 홈·캘린더·분석이 나눠 쓴다(받는 양은 예전보다 오히려 줄었다).
const MOBILE_CACHE_KEY = 'E2B_MOBILE_CACHE_V1';
const readMobileCache = () => {
  try { return JSON.parse(localStorage.getItem(MOBILE_CACHE_KEY)) || null; } catch (e) { return null; }
};
const writeMobileCache = (data) => {
  try { localStorage.setItem(MOBILE_CACHE_KEY, JSON.stringify(data)); } catch (e) { /* 용량 초과 등은 무시 */ }
};

export default function MobileMain({ user, onLogout }) {
  // 🚀 중복 호출 및 모바일 네트워크 지연에 따른 데이터 누수를 원천 차단하는 상태 보호 플래그
  const isMounted = useRef(true);
  const mainRef = useRef(null);   // 하단 바에서 '맨 위로' 올릴 때 쓴다

  // 화면 버전 — PC 상단 바와 같은 훅을 써서 같은 값이 나오게 한다 (2026-09-23)
  const { label: versionLabel } = useAppVersion();

  // 🚀 [역할 등급별 메뉴 권한 필터링] 
  // 로그인한 유저의 권한이 'RECALL'(리콜 수행자)인 경우 오직 리콜 메뉴만 배열에 담습니다.
  const isRecallUser = user?.role === 'RECALL';
  const isViewerUser = user?.role === 'VIEWER';  // 외부 열람 계정: 집계 화면만(고객명 나오는 캘린더·리콜 제외)
  
  const mobileMenus = isRecallUser 
    ? [{ id: 'recall', label: '🔧 리콜' }]
    : isViewerUser
      ? [{ id: 'dashboard', label: '🏠 홈' }, { id: 'analysis', label: '📊 분석' }]
      : [
        { id: 'dashboard', label: '🏠 홈' },
        { id: 'calendar', label: '📅 캘린더' },
        { id: 'analysis', label: '📊 분석' },
        { id: 'recall', label: '🔧 리콜' }
      ];

  // 🚀 리콜 수행자는 첫 진입 화면을 'recall'로 고정하고, 일반 직원은 'dashboard'로 시작합니다.
  const [activeTab, setActiveTab] = useState(isRecallUser ? 'recall' : 'dashboard');
  
  // 🚀 캐시가 있으면 첫 화면부터 즉시 보여준다(로딩 스피너 생략).
  const cached = readMobileCache();

  const [asList, setAsList] = useState(cached?.asList || []);
  const [vehicles, setVehicles] = useState(cached?.vehicles || []);
  const [parts, setParts] = useState(cached?.parts || []);
  const [customerCount, setCustomerCount] = useState(cached?.customerCount || 0);
  const [executions, setExecutions] = useState(cached?.executions || []);
  const [engineers, setEngineers] = useState(cached?.engineers || []);
  const [loading, setLoading] = useState(!cached);
  const [showPwModal, setShowPwModal] = useState(false);

  const loadAllMobileData = async () => {
    // 캐시로 이미 보여주고 있으면 스피너 없이 조용히 갱신한다(loading 초기값이 처리).
    try {
      const [v, custCount, p, a, execs, engs] = await Promise.all([
        vehicleService.getVehicles().catch(() => []),
        customerService.countCustomers().catch(() => 0),        // 고객은 개수만(문서 다운로드 안 함)
        partService.getParts().catch(() => []),
        asService.getRecords().catch(() => []),                 // AS 전 이력 (홈·캘린더·분석이 함께 쓴다)
        recallService.getRecallExecutions().catch(() => []),
        engineerService.getEngineers().catch(() => [])
      ]);

      // 데이터 로드가 끝났을 때 컴포넌트가 꺼진 상태라면 비동기 메모리 바인딩 취소 (메모리 누수 및 중복 조회의 근본적 방어)
      if (!isMounted.current) return;

      const safeAs = a || [];
      const safeVeh = v || [];
      const safeParts = p || [];
      setAsList(safeAs);
      setVehicles(safeVeh);
      setParts(safeParts);
      setCustomerCount(Number(custCount) || 0);
      setExecutions(execs || []);
      setEngineers(engs || []);

      // 다음 접속 때 즉시 보여줄 수 있도록 캐시에 저장한다.
      writeMobileCache({
        asList: safeAs, vehicles: safeVeh, parts: safeParts, customerCount: Number(custCount) || 0,
        executions: execs || [], engineers: engs || []
      });

    } catch (e) { console.error(e); }
    
    if (isMounted.current) {
      setLoading(false);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    loadAllMobileData();
    
    return () => {
      isMounted.current = false;
    };
  }, [user]); // 유저 권한이 변경되거나 재접속 시 데이터 초기화

  const handleRecallActionSave = async (execId, carNumber, payload) => {
    try {
      await recallService.saveVehicleAction(execId, carNumber, payload);
      alert("✅ 조치 내역이 정상 연동 저장되었습니다.");
      await loadAllMobileData();
    } catch (e) { alert("저장 실패"); }
  };

  const renderActiveView = () => {
    switch (activeTab) {
      case 'dashboard':
        return <MobileDashboard asList={asList} loading={loading} />;
      case 'calendar':
        return <MobileAsCalendar asList={asList} loading={loading} />;
      case 'analysis':
        return <MobileAnalysis asRecords={asList} vehicles={vehicles} parts={parts} customerCount={customerCount} loading={loading} />;
      case 'recall':
        return <MobileRecall executions={executions} engineers={engineers} onActionSave={handleRecallActionSave} loading={loading} />;
      default:
        return isRecallUser 
          ? <MobileRecall executions={executions} engineers={engineers} onActionSave={handleRecallActionSave} loading={loading} />
          : <MobileDashboard asList={asList} loading={loading} />;
    }
  };

  return (
    <div className="m-shell" style={{ display: 'flex', flexDirection: 'column', width: '100%', backgroundColor: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', overflow: 'hidden' }}>
      
      {/* 상단 통합 네비게이션 프레임 바 배치 */}
      <div style={{ position: 'sticky', top: 0, zIndex: 1000, background: '#ffffff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        
        {/* 타이틀 영역 */}
        <div style={{ display: 'flex', alignItems: 'center', justifycontent: 'space-between', padding: '12px 16px', background: '#1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <span style={{ fontSize: '11px', background: '#ef4444', color: '#ffffff', padding: '2px 6px', borderRadius: '6px', fontWeight: 'bold', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {isRecallUser ? 'RECALL ONLY' : versionLabel}
            </span>
            <span style={{ fontSize: '14px', fontWeight: '900', color: '#ffffff', letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isRecallUser ? `${user?.name || '수행자'} - 리콜 전용 관제` : 'CRM 모바일 관제 시스템'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: 'auto' }}>
            <button onClick={() => setShowPwModal(true)} title="비밀번호 변경" style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '16px', cursor: 'pointer', padding: '4px' }}>🔑</button>
            <button onClick={() => { if(window.confirm("로그아웃 하시겠습니까?")) onLogout(); }} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '16px', cursor: 'pointer', padding: '4px' }}>🚪</button>
          </div>
        </div>

        {/* 🚀 [권한 분기 완수] 상단 메뉴 탭바 필터 바인딩 */}
        <div style={{ display: 'flex', background: '#ffffff', padding: '2px 4px', justifyContent: 'space-between' }}>
          {mobileMenus.map((menu) => {
            const isActive = activeTab === menu.id;
            return (
              <button
                key={menu.id}
                onClick={() => setActiveTab(menu.id)}
                style={{
                  flex: 1,
                  padding: '12px 0',
                  border: 'none',
                  background: 'none',
                  fontSize: '13px',
                  fontWeight: isActive ? '900' : '600',
                  color: isActive ? '#ef4444' : '#64748b', // 리콜 전용일 때는 강조색을 레드로 매핑하여 직관성 확보
                  borderBottom: isActive ? (isRecallUser ? '3px solid #ef4444' : '3px solid #007bff') : '3px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  outline: 'none'
                }}
              >
                {menu.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 🚀 외부 공개용 읽기 전용 계정 안내 */}
      {user?.role === 'VIEWER' && (
        <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', color: '#92400e', padding: '10px 14px', fontSize: '12px', fontWeight: '700', flexShrink: 0 }}>
          👁️ 읽기 전용(모니터링) 계정 — 조회만 가능합니다.
        </div>
      )}

      {/* 독립 본문 컨텐츠 뷰 스크롤 영역 */}
      <main ref={mainRef} style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {renderActiveView()}
      </main>

      {/* 하단 이동 메뉴 — VIMS 와 같은 형태.
          외부 공개용 읽기 전용 계정에는 사내 시스템 링크를 보여주지 않는다. */}
      {!isViewerUser && (
        <MobileSystemNav
          current="crm"
          onCurrentClick={() => mainRef.current && mainRef.current.scrollTo({ top: 0, behavior: 'smooth' })}
        />
      )}

      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
    </div>
  );
}